'use client';

import HotIcon from '@/components/Icons/HotIcon';
import MarketTopic from '@/components/predic/detail/event/MarketTopic';
import { useRightDrawer } from '@/contexts/DrawerContext';
import { useEventStreaming } from '@/hooks/use-event-streaming';
import { useTranslation } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { Skeleton } from '@mui/material';
import { ChevronRight } from 'lucide-react';

export default function EventCard({ sourceId }: { sourceId: string }) {
  const { t } = useTranslation();
  const { openDrawer, setDrawerWidth } = useRightDrawer();

  const { event, isLoading } = useEventStreaming(sourceId);

  const handleEvent = () => {
    if (sourceId && !isLoading) {
      setDrawerWidth(600);
      openDrawer(<MarketTopic id={sourceId} position="right" />);
    }
  };

  const poster =
    event?.image_url || event?.news?.flatMap((n) => n.events).find((e) => e?.image)?.image || null;

  if (isLoading) {
    return (
      <div
        className={cn(
          'rounded-xl overflow-hidden border border-[#1E1E1E] bg-[#121212] max-w-[390px]',
          'p-4 my-5 space-y-3 hover:text-[#b7b9bc]',
          'cursor-pointer transition-colors duration-200',
        )}
      >
        <div className="flex items-center gap-2.5">
          <Skeleton variant="rounded" width={44} height={44} sx={{ bgcolor: 'grey.800' }} />
          <div className="flex-1">
            <Skeleton variant="text" width={'80%'} sx={{ fontSize: '1rem', bgcolor: 'grey.800' }} />
            <Skeleton variant="text" width={'60%'} sx={{ fontSize: '1rem', bgcolor: 'grey.800' }} />
          </div>
        </div>
        <div className="text-xs flex items-center gap-2">
          <Skeleton
            variant="text"
            width={'80%'}
            sx={{ fontSize: '0.75rem', bgcolor: 'grey.800' }}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl w-full block h-[130px] bg-cover! my-5 max-w-[390px]"
      style={{ background: `url(${poster}) no-repeat center` }}
      onClick={handleEvent}
    >
      <div className="flex flex-col justify-between p-4 h-full bg-linear-to-r from-black/60 to-transparent">
        <h5 className="text-lg leading-6 font-bold line-clamp-2">{event?.title}</h5>
        <div className="flex items-center justify-between text-sm leading-6">
          <div className="flex items-center gap-2 h-6 rounded-md px-2 bg-[#DA0473]">
            <HotIcon size={14} />
            <span className="font-bold">{t('HOT')}</span>
          </div>
          {event?.category && (
            <span className="bg-black/60 rounded-md h-6 px-2 capitalize ml-2">
              {event.category.replace(/_/g, ' ')}
            </span>
          )}
          <div className="ml-auto flex items-center gap-1 font-medium text-white">
            {t('More')} <ChevronRight strokeWidth={3} size={20} />
          </div>
        </div>
      </div>
    </div>
  );
}
